import React, {
  useEffect, useMemo, useState } from 'react';
import {
  PHONE_COUNTRIES,
  type DocumentElement as DocumentElementType,
} from '@eaco/forms-core';
import {
  FieldHeader,
  FieldHelpText,
  FIELD_SHELL_CLASS,
  FIELD_BARE_INPUT_CLASS,
} from './FieldParts';
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover';
import { cn } from '../lib/utils';
import {
  useElementValue,
  useIsReadOnly,
} from '../DocumentView/DocumentContext';
import HiddenElementBanner from './HiddenElementBanner';
import ArchivedElementBanner from './ArchivedElementBanner';
import { useDefaultValue } from './useDefaultValue';

interface Props {
  element: DocumentElementType;
}

interface Country {
  code: string;
  dial: string;
  name: string;
}

// Curated country list (dial code + name). Enough to cover the common cases
// without shipping a full ISO dataset; the flag is derived from the code.
const COUNTRIES: Country[] = PHONE_COUNTRIES;

const DEFAULT_COUNTRY = COUNTRIES[0];

/** Country-code → flag emoji via Unicode regional indicators. */
const flagEmoji = (code: string): string =>
  code
    .toUpperCase()
    .replace(/./g, (c) => String.fromCodePoint(127397 + c.charCodeAt(0)));

/** Longest dial-code prefix match, so "+61" wins over "+6" and "+1" resolves. */
const matchDial = (v: string): Country | undefined =>
  COUNTRIES.filter((c) => v.startsWith(c.dial)).sort(
    (a, b) => b.dial.length - a.dial.length,
  )[0];

const PhoneInput: React.FC<Props> = ({ element }) => {
  const [value, setValue] = useElementValue<string>(element.id);
  const isReadOnly = useIsReadOnly();
  const data = element.data as {
    label?: string;
    subLabel?: string;
    placeholder?: string;
    required?: boolean;
    helpText?: string;
    defaultValue?: string;
    defaultCountry?: string;
    hidden?: boolean;
  };
  const displayValue = useDefaultValue(value, setValue, data?.defaultValue);
  const label = data?.label || 'Phone';
  const subLabel = data?.subLabel;
  const placeholder = data?.placeholder || 'Enter phone number...';
  const required = data?.required || false;
  const helpText = data?.helpText;

  // Selected country is sticky UI state — seeded from the stored value's dial
  // prefix, or the configured default, or AU.
  const [country, setCountry] = useState<Country>(() => {
    const configured = data?.defaultCountry
      ? COUNTRIES.find((c) => c.code === data.defaultCountry)
      : undefined;
    return matchDial((value ?? '').trim()) || configured || DEFAULT_COUNTRY;
  });
  const [pickerOpen, setPickerOpen] = useState(false);
  const [search, setSearch] = useState('');

  // A value written from outside (voice fill, a resumed draft) may carry a
  // different dial code than the sticky selector; follow it so the next edit
  // keeps that country instead of re-prefixing with the old one.
  useEffect(() => {
    const stored = (value ?? '').trim();
    const dialled = matchDial(stored);
    // Countries can share a code (+1): keep the current one while it still fits.
    if (dialled && !stored.startsWith(country.dial)) setCountry(dialled);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  // The local (national) number = stored value with any dial-code prefix
  // stripped, so switching country re-prefixes cleanly.
  const localNumber = useMemo(() => {
    const t = (displayValue ?? '').trim();
    const m = matchDial(t);
    return m ? t.slice(m.dial.length).trim() : t;
  }, [displayValue]);

  // Store the full "+61 <number>" string; empty number → empty value so
  // required-checks still see it as unanswered.
  const commit = (dial: string, num: string) => {
    const n = num.trim();
    setValue(n ? `${dial} ${n}` : '');
  };

  const filtered = COUNTRIES.filter((c) => {
    const q = search.trim().toLowerCase();
    if (!q) return true;
    return (
      c.name.toLowerCase().includes(q) ||
      c.dial.includes(q) ||
      c.code.toLowerCase().includes(q)
    );
  });

  // Read-only (e.g. a completed result): render the stored number as text.
  if (isReadOnly) {
    return (
      <div className="space-y-2">
        <FieldHeader
          htmlFor={element.id}
          label={label}
          subLabel={subLabel}
          required={required}
        />
        <p className="text-sm">
          {displayValue ? (
            displayValue
          ) : (
            <span className="text-muted-foreground">—</span>
          )}
        </p>
        <FieldHelpText>{helpText}</FieldHelpText>
      </div>
    );
  }

  return (
    <div className="space-y-2">
      <FieldHeader
        htmlFor={element.id}
        label={label}
        subLabel={subLabel}
        required={required}
      />
      <div className={cn(FIELD_SHELL_CLASS, 'items-stretch')}>
        <Popover open={pickerOpen} onOpenChange={setPickerOpen}>
          <PopoverTrigger asChild>
            <button
              type="button"
              className="flex flex-none items-center gap-[6px] border-r border-[var(--forms-border-light)] bg-[var(--forms-addon-bg)] px-[12px] text-[13px] font-semibold text-[var(--forms-ink)] outline-none"
            >
              <span className="text-[15px] leading-none">
                {flagEmoji(country.code)}
              </span>
              <span>{country.dial}</span>
              <span className="text-[var(--forms-optional)]">
                <svg
                  width="12"
                  height="12"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth={2.4}
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  aria-hidden
                >
                  <polyline points="6 9 12 15 18 9" />
                </svg>
              </span>
            </button>
          </PopoverTrigger>
          <PopoverContent align="start" className="w-[260px] p-0">
            <div className="border-b p-2">
              <input
                autoFocus
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search countries…"
                className="w-full rounded-[8px] border border-[var(--forms-border)] bg-white px-[10px] py-[7px] text-[13px] text-[var(--forms-ink)] placeholder:text-[var(--forms-faint)] focus:outline-none focus:border-[var(--forms-accent,var(--forms-accent-default))]"
              />
            </div>
            <ul className="max-h-[240px] overflow-y-auto py-1">
              {filtered.map((c) => (
                <li key={`${c.code}-${c.dial}`}>
                  <button
                    type="button"
                    onClick={() => {
                      setCountry(c);
                      commit(c.dial, localNumber);
                      setPickerOpen(false);
                      setSearch('');
                    }}
                    className={cn(
                      'flex w-full items-center gap-[10px] px-[12px] py-[8px] text-left text-[13px] hover:bg-[var(--forms-addon-bg)]',
                      c.code === country.code &&
                        'bg-[var(--forms-accent-soft,var(--forms-accent-soft-default))]',
                    )}
                  >
                    <span className="text-[15px] leading-none">
                      {flagEmoji(c.code)}
                    </span>
                    <span className="flex-1 truncate text-[var(--forms-ink)]">
                      {c.name}
                    </span>
                    <span className="text-[var(--forms-muted)]">{c.dial}</span>
                  </button>
                </li>
              ))}
              {filtered.length === 0 && (
                <li className="px-[12px] py-[10px] text-[13px] text-[var(--forms-muted-soft)]">
                  No matches
                </li>
              )}
            </ul>
          </PopoverContent>
        </Popover>
        <input
          id={element.id}
          type="tel"
          placeholder={placeholder}
          value={localNumber}
          onChange={(e) => commit(country.dial, e.target.value)}
          className={cn(FIELD_BARE_INPUT_CLASS, 'px-[var(--forms-input-px)]')}
        />
      </div>
      <FieldHelpText>{helpText}</FieldHelpText>
      <HiddenElementBanner hidden={data?.hidden} />
      <ArchivedElementBanner archived={element.archived} />
    </div>
  );
};

export default PhoneInput;
